/**
 * ThemeInheritance.ts - Theme inheritance system
 * 
 * CHANGE HISTORY:
 * - 251102_1715 danielbeer: Created for Phase 3.5b - Advanced Features
 * 
 * Allows themes to extend other themes with variable overrides
 */

import type { StyleVariables } from '../types';

export interface Theme {
  id: string;
  name: string;
  extends?: string; // Parent theme ID
  variables: StyleVariables;
  metadata?: Record<string, any>;
}

export class ThemeInheritanceManager {
  private themes: Map<string, Theme>;

  constructor() {
    this.themes = new Map();
  }

  /**
   * Add theme
   */
  addTheme(theme: Theme): void {
    this.themes.set(theme.id, theme);
  }

  /**
   * Get theme
   */
  getTheme(id: string): Theme | undefined {
    return this.themes.get(id);
  }

  /**
   * Get resolved theme (with inheritance)
   */
  getResolvedTheme(id: string): Theme | null {
    const theme = this.themes.get(id);
    if (!theme) return null;

    if (!theme.extends) {
      return { ...theme };
    }

    const parent = this.getResolvedTheme(theme.extends);
    if (!parent) {
      return { ...theme };
    }

    return {
      ...theme,
      variables: {
        ...parent.variables,
        ...theme.variables
      }
    };
  }

  /**
   * Get all themes
   */
  getAllThemes(): Theme[] {
    return Array.from(this.themes.values());
  }
}

let themeManagerInstance: ThemeInheritanceManager | null = null;

export function getThemeInheritanceManager(): ThemeInheritanceManager {
  if (!themeManagerInstance) {
    themeManagerInstance = new ThemeInheritanceManager();
  }
  return themeManagerInstance;
}
